/**
 * HTTP Service Abstraction Layer
 * 
 * This service provides a clean HTTP interface that doesn't directly depend on
 * the Vuex store, breaking circular dependencies while maintaining all functionality.
 */

import Axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { CONFIG } from "../config";
import { RawLocation } from "vue-router";
import { LAUNCH_ROUTES_PATHS } from "../../launch/launch-routes";
import { LAUNCH_ROUTES_PATHS_V2_BASE } from "../../launch/v2/launch-routes-v2";
import { useConfigController } from "../../config/useConfigStore";
import { AUTH_STORE_CONST } from "../../auth/auth-store";
import {
  IUserMessage,
  USER_MESSAGE_LEVEL,
} from "../../user-message/user-message-models";
import {
  messageDispatchHelper,
  USER_MESSAGE_STORE_CONST,
} from "../../user-message/user-message-store";
import { ServiceContainer } from "./service-container";

export interface IHttpService {
  get(url: string, config?: AxiosRequestConfig): Promise<any>;
  post(url: string, data?: any, config?: AxiosRequestConfig): Promise<any>;
  put(url: string, data?: any, config?: AxiosRequestConfig): Promise<any>;
  delete(url: string, config?: AxiosRequestConfig): Promise<any>;
  setAuthTokenProvider(provider: () => string | null): void;
  setStoreProvider(provider: () => any): void;
  setRouterProvider(provider: () => any): void;
}

export interface HttpGenericError extends AxiosError {
  message: string;
  stack: string;
}

/**
 * HTTP Service Implementation
 * Provides HTTP functionality with dependency injection for auth and routing
 */
export class HttpService implements IHttpService {
  private axiosInstance: AxiosInstance;
  private authTokenProvider: (() => string | null) | null = null;
  private storeProvider: (() => any) | null = null;
  private routerProvider: (() => any) | null = null;

  constructor(baseURL: string = CONFIG.REST_URL) {
    this.axiosInstance = Axios.create({
      baseURL,
    });

    this.setupInterceptors();
  }

  /**
   * Set the authentication token provider function
   */
  public setAuthTokenProvider(provider: () => string | null): void {
    this.authTokenProvider = provider;
  }

  /**
   * Set the store provider function
   */
  public setStoreProvider(provider: () => any): void {
    this.storeProvider = provider;
  }

  /**
   * Set the router provider function
   */
  public setRouterProvider(provider: () => any): void {
    this.routerProvider = provider;
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor for authentication
    this.axiosInstance.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        // Add JWT token if available
        if (this.authTokenProvider) {
          const token = this.authTokenProvider();
          if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error: AxiosError) => {
        console.error("HTTP request interceptor error:", error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Return data directly to maintain existing API compatibility
        return response.data as any;
      },
      (error: AxiosError) => {
        this.handleResponseError(error);
        return Promise.reject(error);
      }
    );
  }

  /**
   * Handle HTTP response errors
   */
  private handleResponseError(error: AxiosError): void {
    // Add error message to store if available
    if (this.storeProvider) {
      const store = this.storeProvider();
      if (store) {
        store.commit(
          USER_MESSAGE_STORE_CONST.USER_MESSAGE_CONST_MODULE_NAME +
            "/" +
            USER_MESSAGE_STORE_CONST.USER_MESSAGE_MUTATIONS_ADD_MESSAGE,
          {
            level: USER_MESSAGE_LEVEL.INFO,
            message:
              "Error: " +
              (error.response && error.response.status
                ? error.response.status
                : "Server ERROR") +
              (error.response && error.response.data
                ? " - " + (error.response.data as any)?.error
                : ""),
          } as IUserMessage
        );
      }
    }

    // Use message dispatch helper for toast notifications
    const apiMessage: string =
      error.response && error.response.data && (error.response.data as any)?.error
        ? (error.response.data as any).error
        : "";

    messageDispatchHelper(
      error.message + (apiMessage.length > 0 ? ", " + apiMessage : ""),
      USER_MESSAGE_LEVEL.ERROR.toString()
    );

    // Handle 401 authentication errors
    if (error.response && error.response.status === 401) {
      console.log("401: Authentication error");
      this.handleAuthenticationError();
    }

    console.error("HTTP response interceptor error:", error);
  }

  /**
   * Handle authentication errors by redirecting to login
   */
  private handleAuthenticationError(): void {
    if (!this.routerProvider) {
      console.warn("Router provider not available for authentication redirect");
      return;
    }

    const router = this.routerProvider();
    if (!router) {
      console.warn("Router not available for authentication redirect");
      return;
    }

    const configController = useConfigController();
    let redirectPath: string = "";
    
    if (configController.getVersion.value === "v2") {
      redirectPath = LAUNCH_ROUTES_PATHS_V2_BASE;
    } else {
      redirectPath = LAUNCH_ROUTES_PATHS.SHOW_ENTRIES;
    }
    
    console.log("401: Authentication error, redirectPath: " + redirectPath);
    router.push({
      path: redirectPath,
    } as RawLocation);
  }

  /**
   * HTTP GET request
   */
  public async get(url: string, config?: AxiosRequestConfig): Promise<any> {
    return this.axiosInstance.get(url, config);
  }

  /**
   * HTTP POST request
   */
  public async post(url: string, data?: any, config?: AxiosRequestConfig): Promise<any> {
    return this.axiosInstance.post(url, data, config);
  }

  /**
   * HTTP PUT request
   */
  public async put(url: string, data?: any, config?: AxiosRequestConfig): Promise<any> {
    return this.axiosInstance.put(url, data, config);
  }

  /**
   * HTTP DELETE request
   */
  public async delete(url: string, config?: AxiosRequestConfig): Promise<any> {
    return this.axiosInstance.delete(url, config);
  }
}

/**
 * Factory function for creating HTTP service instances
 */
export function createHttpService(): HttpService {
  return new HttpService();
}

/**
 * Get authentication token from store (used as provider function)
 */
export function getAuthTokenFromStore(): string | null {
  try {
    const container = ServiceContainer.getInstance();
    const store = container.getStore();
    
    if (!store) {
      return null;
    }

    const isUserLoggedIn = store.state[AUTH_STORE_CONST.AUTH_CONST_MODULE_NAME].isLoggedIn;
    if (!isUserLoggedIn) {
      return null;
    }

    const userToken = store.state[AUTH_STORE_CONST.AUTH_CONST_MODULE_NAME].tokenReponse?.token;
    return userToken || null;
  } catch (error) {
    console.warn("Failed to get auth token from store:", error);
    return null;
  }
}

/**
 * Get store instance (used as provider function)
 */
export function getStoreFromContainer(): any {
  return ServiceContainer.getInstance().getStore();
}

/**
 * Get router instance (used as provider function)
 */
export function getRouterFromContainer(): any {
  return ServiceContainer.getInstance().getRouter();
}